import {
  TableContainer,
  TableCell,
  TableHead,
  Table,
  TableBody,
  TableRow,
  Checkbox,
  IconButton,
  Stack,
} from '@mui/material'
import { Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { ITenderCommission } from 'types/tender/tenderCommissions'
import {
  useDeleteTenderCommissionMemberMutation,
  useAssignMemberAsChairmanMutation,
} from 'services/tender/tenderCommissions'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { useBoolean } from 'hooks/useBoolean'

type Props = {
  tenderCommission: ITenderCommission
  isDisabled?: boolean
}

export const TenderCommitteeMembersTable = ({
  tenderCommission,
  isDisabled = false,
}: Props) => {
  const { t } = useTranslation()
  const [currentlyDeletingId, setCurrentlyDeletingId] = useState<string | null>()
  const dialogConfirmMemberDelete = useBoolean(false)

  const [deleteTenderCommissionMember, { isSuccess, isError }] =
    useDeleteTenderCommissionMemberMutation()

  const [assignMemberAsChairman] = useAssignMemberAsChairmanMutation()

  const handleDelete = () => {
    if (currentlyDeletingId) {
      deleteTenderCommissionMember({
        comissionId: tenderCommission.id,
        memberId: currentlyDeletingId,
      })
    }
    setCurrentlyDeletingId(null)
  }

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }

    if (isError) {
      toast.success(t('operation-failure'))
    }
  }, [isSuccess, isError, t])

  return (
    <>
      <ConfirmationDialog
        open={dialogConfirmMemberDelete.isTrue}
        onClose={dialogConfirmMemberDelete.setFalse}
        onConfirm={handleDelete}
        text={t('are-you-sure-you-want-to-delete')}
      />
      <TableContainer>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}> {t('fullName')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('email-address')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('position')}</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenderCommission.tenderCommissionMembers?.map((member) => (
              <TableRow key={member.id}>
                <TableCell>{`${member.userInfo?.firstName} ${member.userInfo?.lastName}`}</TableCell>
                <TableCell>{member.userInfo?.email}</TableCell>
                <TableCell>{member.userInfo?.position ?? '-'}</TableCell>
                <TableCell>
                  <Stack alignItems="center" direction="row">
                    {t('chairman')}
                    <Checkbox
                      checked={member.chairman}
                      disabled={isDisabled}
                      onClick={() =>
                        assignMemberAsChairman({
                          comissionId: tenderCommission.id,
                          memberId: member.id,
                        })
                      }
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  {!isDisabled && (
                    <IconButton
                      onClick={() => {
                        dialogConfirmMemberDelete.setTrue()
                        setCurrentlyDeletingId(member.id)
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
